import { type SupabaseClient } from '@supabase/supabase-js';
import { HTTPError, default as ky } from 'ky';
import { debounce } from 'perfect-debounce';
import toast from 'react-hot-toast';
import { descriptionActions } from '../global-state/episode-descriptions';
import {
  addOrUpdateEpisode,
  addOrUpdateEpisodes,
  updatePodcast,
  usePodcastsStore,
} from '../global-state/podcasts';
import {
  Ad,
  Episode,
  EpisodeCompoundId,
  EpisodeFromApi,
  EpisodeStatus,
  Podcast,
  PodcastId,
} from '../models/podcasts';
import debugLog from './debug';
import { reportSentryError } from './error-handling';
import { wait } from './helpers';
import { Modify } from './typescript';

// export const baseUrl = 'http://localhost:3000';
export const baseUrl = 'https://sh-api.adblockpodcast.com';
// export const baseUrl = 'https://cached-api.adblockpodcast.com';

const log = (...args: any[]) => {
  debugLog({ logName: 'api', logColor: 'gray' }, ...args);
};

// Refresh auth session if we get a 401 Unauthorized from API
const refreshAuthSessionDebounced = debounce(() => {
  log('Refreshing auth session');
  toast('Refreshing your session...');
  // @ts-expect-error _supabase global used to avoid circular dependency, sigh.
  return (window._supabase as SupabaseClient).auth
    .refreshSession()
    .then(({ data, error }) => {
      log('Refreshed session result', { data, error });
      if (error) {
        log('Failed to refresh session', error);
      }
    });
}, 100);

export function apiUrl(path: string, params = {}) {
  const url = new URL(`${baseUrl}/${path}`);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value as string);
  });
  return url.toString();
}

export async function requestApi(url: string, postData?: object, headers?: object) {
  const api = headers
    ? ky.extend({
        hooks: {
          beforeRequest: [
            (req) =>
              Object.entries(headers).forEach(([key, value]) =>
                req.headers.set(key, value)
              ),
          ],
        },
      })
    : ky;

  try {
    // Default is 10s, but w/ CORS on a slow network, the 2 roundtrips can take a bit longer sometimes
    const defaultTimeout = 15 * 1000;
    const response = await (postData
      ? api.post(url, {
          headers: { 'Content-Type': 'application/json' },
          json: postData,
          timeout: defaultTimeout,
        })
      : api.get(url, {
          timeout: defaultTimeout,
        }));
    const json = await response.json();
    return { json, response };
  } catch (error: unknown) {
    if (!(error instanceof HTTPError)) {
      // Could be that the request was just cancelled or something
      log('API request failed', error, url);
      throw new RequestFailedError(`API request failed: ${url}`, error);
    }

    let errorResponseJSON;
    let errorMessageFromAPI;
    try {
      errorResponseJSON = await error.response.json();
      errorMessageFromAPI = errorResponseJSON.error;
    } catch (err) {}
    log('API error', error.response.status, errorResponseJSON, error.message);

    // Refresh auth session if we get a 401 Unauthorized from API
    if (error.response.status === 401 && errorMessageFromAPI === 'Unauthorized') {
      refreshAuthSessionDebounced();
    }
    if (errorMessageFromAPI && typeof errorMessageFromAPI === 'string') {
      if (error.response.status === 400) {
        // Debug invalid requests
        log('Invalid request?', url, postData);
      }
      throw new APIError(
        `${errorMessageFromAPI} (Status = ${error.response.status})`,
        error
      );
    }

    throw error;
  }
}

export async function fetchPodcast(id: PodcastId) {
  const url = apiUrl(`podcast/${id}`);

  const { json } = await requestApi(url);
  const { podcast } = json as { podcast: Podcast };

  podcast.episodes = []; // Default value needs to be set
  // @ts-ignore Remove any properties added for debugging
  delete podcast.debug;
  updatePodcast(podcast, 'API');
  return podcast;
}

// Batch podcast episode updates in store to avoid excessive re-renders downstream
// Even just a small delay is enough to make a difference
let batchedTimeoutPromise: Promise<void> | null = null;
function waitToUpdateEpisodesInBatch() {
  if (batchedTimeoutPromise) return batchedTimeoutPromise;

  batchedTimeoutPromise = wait(20).then(() => {
    batchedTimeoutPromise = null;
  });
  return batchedTimeoutPromise;
}

// Fetch all episodes for a podcast (only excerpts, not full descriptions)
export async function fetchEpisodes(podcastId: PodcastId, type: 'latest' | 'all') {
  const url = apiUrl(
    `podcast/${podcastId}/episodes`,
    type === 'latest' ? { limit: 20 } : { allEpisodesWithExcerpts: 'true' }
  );

  // await wait(3 * 1000);
  // console.log('done waiting');

  const { json, response } = await requestApi(url);
  const { episodes: episodesWithoutQueriedAt, hasMoreEpisodes } = json as {
    episodes: Omit<EpisodeFromApi, 'queriedAt'>[]; // .queriedAt must be derived from header
    hasMoreEpisodes: boolean;
  };
  // log('got episodes from API', episodesWithoutQueriedAt.length, podcastId);

  // Derive .queriedAt property from Last-Modified header
  // .queriedAt will soon no longer be passed by API to improve caching duration
  const episodes = setQueriedAt(
    episodesWithoutQueriedAt,
    getLastModifiedSeconds(response)
  );

  if (!episodes.length) throw new Error('Episodes missing');

  await waitToUpdateEpisodesInBatch(); // Artificial delay to batch re-renders

  const { podcasts } = usePodcastsStore.getState();
  let podcast = podcasts[podcastId];

  // Fetch podcast if missing (in case of slow IndexedDB)
  if (!podcast) {
    log('Fetching missing podcast for episodes', podcastId);
    await fetchPodcast(podcastId);
    podcast = usePodcastsStore.getState().podcasts[podcastId];
    if (!podcast) throw new Error('Podcast missing');
  }

  addOrUpdateEpisodes(
    podcastId,
    episodes.map((ep) => new Episode(ep, podcast)),
    'API'
  );

  // Update descriptions if present on response (expected for latest episodes,
  // but not for all episodes)
  if (type === 'latest' && episodes[0].description !== undefined) {
    const descriptions: Record<string, string> = {};
    for (const ep of episodes) {
      if (ep.description !== undefined) descriptions[ep.guidHash] = ep.description;
    }
    descriptionActions.addLatestDescriptions(podcastId, descriptions);
  }

  // console.log('fetched episodes', podcastId, episodes.length, cursorGuidHash);

  return {
    episodes,
    nextCursor: hasMoreEpisodes ? episodes[episodes.length - 1].guidHash : undefined,
  };
}

export type BumpPriorityAction =
  | 'listen_later'
  | 'download_episode'
  | 'play_episode'
  | 'view_episode';

export async function fetchEpisode(
  podcastId: PodcastId,
  episodeGuidHash: string,
  action?: BumpPriorityAction
) {
  const params = action ? { action } : undefined;
  const url = apiUrl(`podcast/${podcastId}/${episodeGuidHash}`, params);
  const { json, response } = await requestApi(url);
  let { episode: episodeWithoutQueriedAt, status } = json as {
    episode: Omit<EpisodeFromApi, 'queriedAt'>; // .queriedAt must be derived from header
    status?: string;
  };

  // Derive .queriedAt property from Last-Modified header
  // .queriedAt will soon no longer be passed by API to improve caching duration
  const [episode] = setQueriedAt(
    [episodeWithoutQueriedAt],
    getLastModifiedSeconds(response)
  );

  await waitToUpdateEpisodesInBatch(); // Artificial delay to batch re-renders

  const { podcasts } = usePodcastsStore.getState();
  let podcast = podcasts[podcastId];

  // Fetch podcast if missing (in case of "Listening History page")
  if (!podcast) {
    log('Fetching missing podcast for episode', podcastId, episodeGuidHash);
    await fetchPodcast(podcastId);
    podcast = usePodcastsStore.getState().podcasts[podcastId];
    if (!podcast) throw new Error('Podcast missing');
  }

  addOrUpdateEpisode(podcastId, new Episode(episode, podcast));

  return { episode, status };
}

function setQueriedAt(
  episodes: Modify<EpisodeFromApi, { queriedAt?: number }>[],
  lastModifiedSeconds?: number
) {
  // Eventually, API will stop setting .queriedAt property and it'll be derived from
  // the Last-Modified header instead.
  if (!lastModifiedSeconds) {
    lastModifiedSeconds =
      (episodes[0] && episodes[0].queriedAt) || Math.round(Date.now() / 1000);
    console.warn(`Missing Last-Modified for episodes`, episodes[0]);
  } else if (
    episodes[0] &&
    episodes[0].queriedAt &&
    Math.abs(episodes[0].queriedAt - lastModifiedSeconds) > 5
  ) {
    // Should never happen
    reportSentryError(
      new Error(
        `Last-Modified doesn't match .queriedAt...: ${lastModifiedSeconds} !~ ${episodes[0].queriedAt}`
      ),
      { ep: episodes[0] }
    );
  }
  episodes.forEach((ep) => {
    ep.queriedAt = lastModifiedSeconds;
  });
  return episodes as EpisodeFromApi[];
}

// Get seconds since epoch from Last-Modified header
function getLastModifiedSeconds(response: Response) {
  // Note that Last-Modified header should be available on all relevant API
  // responses. In some cases it'll be pretty stale since when Cloudflare
  // revalidates a cached response, if the origin sends a 304 back (Fastify Etag),
  // CF will reuse its cached response (containing the original Last-Modified header).
  // You'll see this in the "cf-cache-status: REVALIDATED" response and then in any
  // subsequent "cf-cache-status: HIT" responses until the cached entry expires and
  // you get a "cf-cache-status: MISS" w/ a new Last-Modified header from the origin.
  // If we really cared about when the response was generated, and weren't simply
  // trying to avoid overwriting a stale response with a *different* and newer
  // one, we could try using a dedicated header (e.g. X-Generated-At), assuming
  // Cloudflare would treat it differently in the 304 case above.
  return extractTimestamp('Last-Modified');

  function extractTimestamp(headerName: string): number {
    const header = response.headers.get(headerName);
    if (!header) {
      reportSentryError(new Error(`No ${headerName} header provided`), {
        url: response.url,
        headers: Object.fromEntries(response.headers),
      });
      return Math.round(Date.now() / 1000);
    }
    return new Date(header).getTime() / 1000;
  }
}

export async function fetchDonations() {
  const { json } = await requestApi(
    'https://didgjhjnevdixtjltmho.supabase.co/functions/v1/get-donations'
  );
  const { data } = json as any;
  return data as {
    totalUsedCredits: number;
    totalPaidCents: number;
    podcasts: {
      podcast: { id: string; name: string; artist: string };
      usedCredits: number;
      paidCents: number;
      donations: {
        receiptUrl: string;
        paidCents: number;
        date: string;
      }[];
      note?: string;
    }[];
  };
}

export async function bumpJobPriority(
  priorityBumps: Record<EpisodeCompoundId, number>
) {
  const url = apiUrl(`bump-job-priority`);
  const { podcasts } = usePodcastsStore.getState();

  const postData = Object.entries(priorityBumps).reduce(
    (acc, [episodeCompoundId, bump]) => {
      const [podcastId, episodeGuidHash] = episodeCompoundId.split('|');
      const podcast = podcasts[podcastId];
      const episode = podcast
        ? podcast.episodes.find((ep) => ep.guidHash === episodeGuidHash)
        : null;
      acc[episodeCompoundId as EpisodeCompoundId] = [
        bump,
        episode ? episode.isSaved : false,
        episode ? episode.status : 'pending',
      ];
      return acc;
    },
    {} as Record<EpisodeCompoundId, [number, boolean, EpisodeStatus]>
  );
  const { json } = await requestApi(url, postData);
  const { updates } = json as {
    updates: Record<
      EpisodeCompoundId,
      {
        isSaved?: boolean;
        ads?: Ad[];
        status?: EpisodeStatus;
      }
    >;
  };

  if (Object.keys(updates).length === 0) return;

  // Selectively update episodes
  const updatedPodcasts = { ...podcasts };
  Object.entries(updates).forEach(([episodeCompoundId, update]) => {
    const [podcastId, episodeGuidHash] = episodeCompoundId.split('|');
    const podcast = updatedPodcasts[podcastId];
    if (!podcast) return;

    const episodeIndex = podcast.episodes.findIndex(
      (ep) => ep.guidHash === episodeGuidHash
    );
    if (episodeIndex === -1) return;

    const episode = podcast.episodes[episodeIndex];

    // Swap out episode
    podcasts[podcastId].episodes[episodeIndex] = new Episode(
      {
        ...episode,
        ...update,
        pubDate: episode.pubDate.toISOString(),
        excerpt: episode.excerpt || '',
        omit: episode.omit === true || undefined,
      },
      podcast
    );
  });
  usePodcastsStore.setState({ podcasts: updatedPodcasts });
}

export async function fetchPodcastEpisodeDescriptions(podcastId: PodcastId) {
  const url = apiUrl(`podcast/${podcastId}/episodes`, {
    allEpisodeDescriptions: 'true',
  });
  const { json } = await requestApi(url);
  const { descriptions } = json as { descriptions: Record<string, string> };
  return descriptions;
}

export async function fetchEpisodeTranscript(
  podcastId: PodcastId,
  episodeGuidHash: string
) {
  const url = apiUrl(`podcast/${podcastId}/${episodeGuidHash}/transcript`);
  await wait(3 * 1000);
  const { json } = await requestApi(url);
  return (json as { transcript: [string, string][] }).transcript;
}

export class APIError extends Error {
  constructor(message: string, cause?: Error | unknown) {
    super(message);
    this.name = 'APIError';
    if (cause) this.cause = cause;
  }
}

export class RequestFailedError extends Error {
  constructor(message: string, cause?: Error | unknown) {
    super(message);
    this.name = 'RequestFailedError';
    if (cause) this.cause = cause;
  }
}
